// src/components/work/WorkFilterBar.jsx
// Presentation-only filter bar (controlled).
// ──────────────────────────────────────────────────────────────────────────────
// UI/UX polish only (no functional changes):
// - Compact title search (smaller width).
// - One clean row on wide desktop; wraps naturally when space is tight (no overflow).
// - Portaled dropdowns for Assignees/Clients (never clipped inside shells).
// - Space-saving chips: show at most 1 then "+N".
// - Visuals align with Quote/Invoice (rounded-xl, compact heights).
//
// Emits canonical filters for useWorkFilters():
//   q, status[], type[], from, to, assigneeId[], clientId[]
//
// Notes: dates are local YYYY-MM-DD; no URL writes here.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FiSearch, FiChevronDown, FiRefreshCcw } from 'react-icons/fi';
import * as userService from '../../services/userService';
import * as clientService from '../../services/clientService';

/* ───────────────────────── helpers ───────────────────────── */
const cx = (...a) =>
  a
    .flatMap((x) =>
      typeof x === 'string'
        ? x
        : x && typeof x === 'object'
        ? Object.keys(x).filter((k) => x[k])
        : []
    )
    .join(' ');

const selectBase =
  'h-10 px-3 pr-8 text-sm border rounded-xl bg-white text-slate-700 appearance-none ' +
  'focus:outline-none focus:ring-4 focus:ring-blue-100 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200';

const inputBase =
  'h-10 px-3 text-sm border rounded-xl bg-white text-slate-700 ' +
  'focus:outline-none focus:ring-4 focus:ring-blue-100 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200';

/** Flushable debounce specifically for search input (prevents lost updates) */
function useDebouncedFlushable(fn, delay) {
  const timeoutRef = useRef(null);
  const lastArgsRef = useRef(null);
  const fnRef = useRef(fn);
  useEffect(() => void (fnRef.current = fn), [fn]);

  const call = useCallback(
    (...args) => {
      lastArgsRef.current = args;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        const a = lastArgsRef.current;
        lastArgsRef.current = null;
        if (a) fnRef.current(...a);
      }, delay);
    },
    [delay]
  );

  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      const a = lastArgsRef.current;
      lastArgsRef.current = null;
      if (a) fnRef.current(...a);
    }
  }, []);

  const cancel = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    lastArgsRef.current = null;
  }, []);

  // ensure pending emit isn't lost on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        const a = lastArgsRef.current;
        lastArgsRef.current = null;
        if (a) fnRef.current(...a);
      }
    },
    []
  );

  return { call, flush, cancel };
}

const STATUS_OPTIONS = [
  ['', 'All Statuses'],
  ['new', 'New'],
  ['todo', 'To do'],
  ['in_progress', 'In progress'],
  ['blocked', 'Blocked'],
  ['scheduled', 'Scheduled'],
  ['en_route', 'En route'],
  ['confirmed', 'Confirmed'],
  ['done', 'Done'],
  ['completed', 'Completed'],
  ['invoiced', 'Invoiced'],
  ['cancelled', 'Cancelled'],
  ['no_show', 'No show'],
];

const TYPE_OPTIONS = [
  ['', 'All Types'],
  ['task', 'Task'],
  ['job', 'Job'],
  ['appointment', 'Appointment'],
];

function inferTabFromPath(pathname) {
  if (pathname.startsWith('/work/jobs')) return 'jobs';
  if (pathname.startsWith('/work/schedule')) return 'appointments';
  if (pathname.startsWith('/work/tasks')) return 'tasks';
  return 'all';
}

/* ───────────────────── Async Multi-Select (typeahead, portaled menu) ───────────────────── */
function EntityMultiSelect({
  placeholder,
  selectedIds,
  onChangeIds,
  search,
  fetchByIds,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [hover, setHover] = useState(0);
  const VISIBLE_CHIPS = 1; // save width in the toolbar

  // Local id->label cache
  const [labelMap, setLabelMap] = useState(() => new Map());

  // Anchoring + portal positioning
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, width: 320 });

  const updateMenuPosition = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.max(260, Math.min(420, Math.round(r.width)));
    let top = r.bottom + 6;
    // flip above if needed
    const mh = menuRef.current ? menuRef.current.offsetHeight : 280;
    if (top + mh > window.innerHeight - 8) {
      top = Math.max(8, r.top - mh - 6);
    }
    setMenuStyle({ top, left: r.left, width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onScroll = () => updateMenuPosition();
    const onResize = () => updateMenuPosition();
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updateMenuPosition]);

  // Hydrate labels for preselected IDs
  useEffect(() => {
    let mounted = true;
    async function hydrate() {
      const unknown = (selectedIds || []).filter((id) => !labelMap.has(id));
      if (!unknown.length) return;
      try {
        let rows = [];
        if (typeof fetchByIds === 'function') {
          rows = await fetchByIds(unknown);
        } else {
          rows = unknown.map((id) => ({ id, label: id.slice(0, 6) + '…' }));
        }
        if (!mounted) return;
        setLabelMap((prev) => {
          const next = new Map(prev);
          for (const r of rows || []) next.set(String(r.id), r.label || String(r.id));
          return next;
        });
      } catch {}
    }
    hydrate();
    return () => void (mounted = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, fetchByIds]);

  // Search
  const enabled = term.trim().length >= 2 && open;
  const { data: options = [], isFetching } = useQuery({
    queryKey: ['entity-search', placeholder, term],
    enabled,
    queryFn: async () => {
      const rows = (await search(term.trim())) || [];
      setLabelMap((prev) => {
        const next = new Map(prev);
        for (const r of rows) next.set(String(r.id), r.label || String(r.id));
        return next;
      });
      return rows;
    },
    staleTime: 10_000,
  });

  // Click-away + Esc
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const w = wrapperRef.current;
      const m = menuRef.current;
      if (w && w.contains(e.target)) return;
      if (m && m.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const chips = useMemo(() => selectedIds || [], [selectedIds]);
  const visible = chips.slice(0, VISIBLE_CHIPS);
  const extra = Math.max(0, chips.length - VISIBLE_CHIPS);

  const removeAt = (id) => onChangeIds((selectedIds || []).filter((x) => x !== id));
  const toggleId = (id) => {
    const set = new Set(selectedIds || []);
    set.has(id) ? set.delete(id) : set.add(id);
    onChangeIds(Array.from(set));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Backspace' && term === '' && (selectedIds?.length || 0) > 0) {
      e.preventDefault();
      removeAt(selectedIds[selectedIds.length - 1]);
      return;
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      setHover(0);
      requestAnimationFrame(updateMenuPosition);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHover((i) => Math.min(i + 1, (options?.length || 1) - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHover((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = options[hover];
      if (row) toggleId(String(row.id));
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <>
      <div
        ref={wrapperRef}
        className="relative w-full sm:basis-[220px] sm:grow sm:shrink sm:min-w-[180px]"
        onClick={() => {
          setOpen(true);
          requestAnimationFrame(updateMenuPosition);
        }}
        aria-label={ariaLabel}
      >
        <div
          className={cx(
            'flex h-10 w-full items-center gap-1 rounded-xl border px-2 text-sm',
            'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
          )}
        >
          {/* Chips (no scrollbar; very compact) */}
          <div className="flex flex-1 min-w-0 items-center gap-1 overflow-hidden">
            {visible.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-[2px] text-xs text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700"
              >
                <span className="max-w-[110px] truncate">{labelMap.get(id) || id}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAt(id);
                  }}
                  className="ml-0.5 rounded-full px-1 text-slate-500 hover:bg-slate-200 hover:text-slate-800 dark:hover:bg-slate-700"
                  aria-label="Remove"
                  title="Remove"
                >
                  ×
                </button>
              </span>
            ))}
            {extra > 0 ? (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-[2px] text-xs text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
                +{extra}
              </span>
            ) : null}

            {/* Input */}
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              className="ml-1 min-w-[70px] flex-1 bg-transparent outline-none placeholder:text-slate-400 dark:placeholder:text-slate-500"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>
      </div>

      {/* Portaled dropdown */}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: menuStyle.top,
              left: menuStyle.left,
              width: menuStyle.width,
              zIndex: 60,
            }}
            className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="max-h-64 overflow-auto">
              {isFetching ? (
                <div className="p-3 text-sm text-slate-500">Searching…</div>
              ) : options.length === 0 ? (
                <div className="p-3 text-sm text-slate-500">
                  {term.trim().length < 2 ? 'Type at least 2 characters' : 'No results'}
                </div>
              ) : (
                options.map((row, i) => {
                  const id = String(row.id);
                  const selected = (selectedIds || []).includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onMouseEnter={() => setHover(i)}
                      onClick={() => toggleId(id)}
                      className={cx(
                        'flex w-full items-center justify-between px-3 py-2 text-left text-sm',
                        i === hover ? 'bg-slate-50 dark:bg-slate-800/60' : '',
                        selected
                          ? 'text-indigo-600 dark:text-indigo-400'
                          : 'text-slate-700 dark:text-slate-200'
                      )}
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">{row.label || id}</span>
                        {row.subtitle ? (
                          <span className="truncate text-xs text-slate-500 dark:text-slate-400">
                            {row.subtitle}
                          </span>
                        ) : null}
                      </span>
                      {selected ? <span className="text-xs">✓</span> : null}
                    </button>
                  );
                })
              )}
            </div>

            <div className="border-t border-slate-100 p-2 text-right text-xs text-slate-500 dark:border-slate-800">
              Enter ↵ to select · Esc to close
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

/* ─────────────────────── service adapters ─────────────────────── */
async function searchAssigneesAdapter(q) {
  try {
    const rows = await userService.listUsers({ search: q, includeInvited: true, limit: 10 });
    const list = Array.isArray(rows) ? rows : rows?.items || rows?.users || [];
    return (list || [])
      .map((u) => ({
        id: String(u.id ?? u._id ?? ''),
        label: u.name || u.email || '(unnamed)',
        subtitle: u.email || undefined,
      }))
      .filter((r) => r.id);
  } catch {
    return [];
  }
}
async function fetchAssigneesByIdsAdapter(ids = []) {
  if (!ids.length) return [];
  try {
    const rows = await userService.getUsersByIds(ids);
    const list = Array.isArray(rows) ? rows : rows?.items || rows || [];
    return (list || [])
      .map((u) => ({
        id: String(u.id ?? u._id ?? ''),
        label: u.name || u.email || '(unnamed)',
        subtitle: u.email || undefined,
      }))
      .filter((r) => r.id);
  } catch {
    return ids.map((id) => ({ id: String(id), label: String(id).slice(0, 6) + '…' }));
  }
}
async function searchClientsAdapter(q) {
  try {
    const res = await clientService.listClients({ search: q, q, limit: 10 });
    const list = Array.isArray(res) ? res : res?.items || res?.clients || [];
    return (list || [])
      .map((c) => ({
        id: String(c.id ?? c._id ?? ''),
        label: c.name || c.displayName || c.company || '(unnamed client)',
        subtitle: c.email || c.phone || undefined,
      }))
      .filter((r) => r.id);
  } catch {
    return [];
  }
}
async function fetchClientsByIdsAdapter(ids = []) {
  if (!ids.length) return [];
  try {
    const rows = await Promise.all(
      ids.map((id) => clientService.getClientById(id).catch(() => null))
    );
    return rows
      .filter(Boolean)
      .map((c) => ({
        id: String(c.id ?? c._id ?? ''),
        label: c.name || c.displayName || c.company || '(unnamed client)',
        subtitle: c.email || c.phone || undefined,
      }))
      .filter((r) => r.id);
  } catch {
    return ids.map((id) => ({ id: String(id), label: String(id).slice(0, 6) + '…' }));
  }
}

/* ───────────────────────── component ───────────────────────── */
export default function WorkFilterBar({ value, onChange }) {
  const location = useLocation();

  const v = value || {};
  const tab = v.tab || inferTabFromPath(location.pathname);
  const showType = tab === 'all';

  const q = v.q ?? '';
  const statusArr = Array.isArray(v.status) ? v.status : [];
  const typeArr = Array.isArray(v.type) ? v.type : [];
  const from = v.from || '';
  const to = v.to || '';
  const assigneeIdArr = Array.isArray(v.assigneeId) ? v.assigneeId : [];
  const clientIdArr = Array.isArray(v.clientId) ? v.clientId : [];

  // Local mirrors for debounce-friendly inputs
  const [qLocal, setQLocal] = useState(q);
  const [fromLocal, setFromLocal] = useState(from);
  const [toLocal, setToLocal] = useState(to);

  useEffect(() => setQLocal(q), [q]);
  useEffect(() => setFromLocal(from), [from]);
  useEffect(() => setToLocal(to), [to]);

  const qDebounce = useDebouncedFlushable((nextQ) => {
    const trimmed = String(nextQ ?? '').trim();
    const current = String(q ?? '').trim();
    if (trimmed === current) return;
    onChange?.({ q: trimmed });
  }, 300);

  const emitStatus = (val) => onChange?.({ status: val ? [val] : [] });
  const emitType = (val) => onChange?.({ type: val ? [val] : [] });

  const onChangeStatus = (e) => emitStatus(e.target.value);
  const onChangeType = (e) => emitType(e.target.value);

  const onChangeFrom = (e) => {
    const val = e.target.value;
    setFromLocal(val);
    onChange?.({ from: val || undefined });
  };
  const onChangeTo = (e) => {
    const val = e.target.value;
    setToLocal(val);
    onChange?.({ to: val || undefined });
  };

  const onReset = () => {
    setQLocal('');
    setFromLocal('');
    setToLocal('');
    qDebounce.cancel();
    onChange?.({
      q: '',
      status: [],
      type: [],
      assigneeId: [],
      clientId: [],
      from: '',
      to: '',
    });
  };

  const statusValue = statusArr[0] || '';
  const typeValue = typeArr[0] || '';

  return (
    // Embedded variant (Option A):
    // Parent Work shell already provides the "card" container. This keeps Work aligned with Quotes/Invoices
    // and prevents the double-card/overlay look.
    <div className="w-full min-w-0">
      {/* Wide desktop tends to stay one row; wraps automatically when space is tight (prevents overflow). */}
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        {/* Title search */}
        <div className="relative w-full sm:w-56 md:w-60 shrink-0">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            inputMode="search"
            placeholder="Search title…"
            className={cx(inputBase, 'pl-9 w-full')}
            value={qLocal}
            onChange={(e) => {
              const val = e.target.value;
              setQLocal(val);
              qDebounce.call(val);
            }}
            onBlur={() => qDebounce.flush()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                qDebounce.flush();
              }
            }}
            aria-label="Search work by title"
            title="Search title"
          />
        </div>

        {/* Status */}
        <div className="relative w-full sm:w-auto shrink-0">
          <select
            className={selectBase}
            value={statusValue}
            onChange={onChangeStatus}
            aria-label="Status"
            title="Filter by status"
          >
            {STATUS_OPTIONS.map(([v, label]) => (
              <option key={v || 'all'} value={v}>
                {label}
              </option>
            ))}
          </select>
          <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
        </div>

        {/* Type (only on All tab) */}
        {showType && (
          <div className="relative w-full sm:w-auto shrink-0">
            <select
              className={selectBase}
              value={typeValue}
              onChange={onChangeType}
              aria-label="Type"
              title="Filter by type"
            >
              {TYPE_OPTIONS.map(([v, label]) => (
                <option key={v || 'all'} value={v}>
                  {label}
                </option>
              ))}
            </select>
            <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
          </div>
        )}

        {/* From / To */}
        <input
          className={cx(inputBase, 'w-full sm:w-36 shrink-0')}
          type="date"
          value={fromLocal}
          onChange={onChangeFrom}
          aria-label="From date"
          title="From date"
          placeholder="yyyy-mm-dd"
        />
        <input
          className={cx(inputBase, 'w-full sm:w-36 shrink-0')}
          type="date"
          value={toLocal}
          onChange={onChangeTo}
          aria-label="To date"
          title="To date"
          placeholder="yyyy-mm-dd"
        />

        {/* Assignees (multi-select) */}
        <EntityMultiSelect
          placeholder="Assignees…"
          ariaLabel="Assignees"
          selectedIds={assigneeIdArr}
          onChangeIds={(ids) => onChange?.({ assigneeId: ids })}
          search={searchAssigneesAdapter}
          fetchByIds={fetchAssigneesByIdsAdapter}
        />

        {/* Clients (multi-select) */}
        <EntityMultiSelect
          placeholder="Clients…"
          ariaLabel="Clients"
          selectedIds={clientIdArr}
          onChangeIds={(ids) => onChange?.({ clientId: ids })}
          search={searchClientsAdapter}
          fetchByIds={fetchClientsByIdsAdapter}
        />

        {/* Reset */}
        <button
          type="button"
          onClick={onReset}
          className={cx(
            'inline-flex items-center gap-2 h-10 px-3 text-sm border rounded-xl bg-white text-slate-700 hover:bg-slate-50 shrink-0',
            'dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200'
          )}
          title="Reset filters"
        >
          <FiRefreshCcw /> Reset
        </button>
      </div>
    </div>
  );
}
